import SystemSettingsDTO, {SystemSettingsCollection, SystemSettingsGroup} from '@/models/SystemSettingsDTO';

export const SETTING_TYPE_MULTISELECT = 'multiselect';
export const SETTING_TYPE_JSON = 'json';
export const SETTING_TYPE_TEXT = 'text';
export const SETTING_TYPE_INT = 'int';
export const SETTING_TYPE_BOOLEAN = 'boolean';

export const OPTIONS_SOURCE_PAYMENT_METHODS = 'payment_methods';
export const OPTIONS_SOURCE_VIET_QR_METHODS = 'viet_qr_methods';
export const OPTIONS_SOURCE_BR_QR_METHODS = 'br_qr_methods';

export const UNCATEGORIZED = 'uncategorized';
export const PAYMENT_PAGE_CATEGORY = 'payment_page';

const PAYMENT_PAGE_SETTING_IDS = new Set([
    'PAYMENT_PAGE_AVAILABLE_LAYOUTS',
    'PSB_CROSS_BORDER_PAGE_DESIGN_ENABLED',
    'HIDE_CROSS_BORDER_ORDER_ID_ENABLED',
    'VIET_QR_VND_AMOUNT_WARNING_ENABLED',
]);

const LEGACY_OPTIONS_SOURCE_BY_ID: Record<string, string> = {
    VIET_QR_AVAILABLE_BANKS: OPTIONS_SOURCE_VIET_QR_METHODS,
    BR_QR_AVAILABLE_BANKS: OPTIONS_SOURCE_BR_QR_METHODS,
};

const STORE_OPTIONS_FILTER_BY_SOURCE: Record<string, [string, string]> = {
    [OPTIONS_SOURCE_VIET_QR_METHODS]: ['RUB', 'VIET_QR'],
    [OPTIONS_SOURCE_BR_QR_METHODS]: ['RUB', 'SBP'],
};

const PAY_METHOD_OPTIONS_SOURCES = new Set([
    OPTIONS_SOURCE_PAYMENT_METHODS,
    OPTIONS_SOURCE_VIET_QR_METHODS,
    OPTIONS_SOURCE_BR_QR_METHODS,
]);

const TYPE_SORT_WEIGHT: Record<string, number> = {
    [SETTING_TYPE_MULTISELECT]: 1,
    [SETTING_TYPE_JSON]: 2,
    [SETTING_TYPE_TEXT]: 3,
    [SETTING_TYPE_INT]: 3,
    [SETTING_TYPE_BOOLEAN]: 4,
};

export const getSystemSettingsItems = (settings: SystemSettingsCollection): SystemSettingsDTO[] =>
    Array.isArray(settings) ? settings : Object.values(settings ?? {});

export const getSystemSettingId = (setting: Pick<SystemSettingsDTO, 'id'> & { key?: string }): string =>
    setting.id ?? setting.key ?? '';

export const getSystemSettingById = (
    settings: SystemSettingsCollection,
    id: string,
): SystemSettingsDTO | undefined =>
    getSystemSettingsItems(settings).find((setting) => getSystemSettingId(setting) === id);

const isTruthyBooleanSettingValue = (value: string | undefined): boolean =>
    value === '1' || value === 'true';

export const getSystemSettingType = (setting: SystemSettingsDTO): string => {
    if (setting.type !== undefined && TYPE_SORT_WEIGHT[setting.type] !== undefined) {
        return setting.type;
    }

    return SETTING_TYPE_TEXT;
};

export const isBooleanSystemSetting = (setting: SystemSettingsDTO): boolean =>
    getSystemSettingType(setting) === SETTING_TYPE_BOOLEAN;

export const isMultiselectSystemSetting = (setting: SystemSettingsDTO): boolean =>
    getSystemSettingType(setting) === SETTING_TYPE_MULTISELECT;

export const isJsonSystemSetting = (setting: SystemSettingsDTO): boolean =>
    getSystemSettingType(setting) === SETTING_TYPE_JSON;

export const isNumericSystemSetting = (setting: SystemSettingsDTO): boolean =>
    getSystemSettingType(setting) === SETTING_TYPE_INT;

export const useNumberInput = (setting: SystemSettingsDTO): boolean =>
    isNumericSystemSetting(setting)
    && (setting.value === '' || /^-?\d+$/.test(String(setting.value)));

export const isPaymentPageSystemSetting = (setting: SystemSettingsDTO): boolean =>
    setting.category === PAYMENT_PAGE_CATEGORY || PAYMENT_PAGE_SETTING_IDS.has(getSystemSettingId(setting));

export const getSettingOptionsSource = (setting: SystemSettingsDTO): string | null =>
    setting.optionsSource ?? LEGACY_OPTIONS_SOURCE_BY_ID[getSystemSettingId(setting)] ?? null;

export const getStoreOptionsFilter = (setting: SystemSettingsDTO): [string, string] | null => {
    const source = getSettingOptionsSource(setting);

    return source === null ? null : STORE_OPTIONS_FILTER_BY_SOURCE[source] ?? null;
};

export const isPayMethodOptionsSetting = (setting: SystemSettingsDTO): boolean => {
    const source = getSettingOptionsSource(setting);

    return source !== null && PAY_METHOD_OPTIONS_SOURCES.has(source);
};

export const parseListSettingValue = (value: string | undefined): string[] => {
    if (!value) {
        return [];
    }

    try {
        const parsed = JSON.parse(value);
        if (!Array.isArray(parsed)) {
            return [];
        }

        return parsed
            .filter((item) => typeof item === 'string' || typeof item === 'number')
            .map((item) => String(item));
    } catch {
        return [];
    }
};

export const parseBankListSettingValue = parseListSettingValue;

export const isSystemSettingEnabled = (settings: SystemSettingsCollection, id: string): boolean =>
    isTruthyBooleanSettingValue(getSystemSettingById(settings, id)?.value);

export const isSettingChecked = (setting: SystemSettingsDTO): boolean =>
    isTruthyBooleanSettingValue(setting.value);

export const getBooleanSettingValue = (_id: string, checked: boolean): string =>
    checked ? '1' : '0';

export const normalizeSystemSettingValue = (
    setting: SystemSettingsDTO,
    rawValue: string | boolean | number | string[] | null | undefined,
): { value: string } => {
    const id = getSystemSettingId(setting);
    const type = getSystemSettingType(setting);

    if (type === SETTING_TYPE_BOOLEAN) {
        if (typeof rawValue === 'boolean') {
            return {value: getBooleanSettingValue(id, rawValue)};
        }

        if (rawValue === null || rawValue === undefined) {
            return {value: getBooleanSettingValue(id, false)};
        }

        return {value: getBooleanSettingValue(id, isTruthyBooleanSettingValue(String(rawValue)))};
    }

    if (type === SETTING_TYPE_MULTISELECT) {
        const list = Array.isArray(rawValue)
            ? rawValue
            : parseListSettingValue(rawValue === null || rawValue === undefined ? undefined : String(rawValue));
        return {value: JSON.stringify(list)};
    }

    if (rawValue === null || rawValue === undefined) {
        return {value: ''};
    }

    return {value: String(rawValue)};
};

export const getSystemSettingsGroups = (settings: SystemSettingsCollection): SystemSettingsGroup[] => {
    const groups = new Map<string, SystemSettingsGroup>();

    getSystemSettingsItems(settings).forEach((setting) => {
        const category = setting.category ?? UNCATEGORIZED;

        if (!groups.has(category)) {
            groups.set(category, {
                category,
                title: setting.categoryTitle ?? '',
                items: [],
            });
        }

        groups.get(category)?.items.push(setting);
    });

    const sorted = Array.from(groups.values());
    sorted.forEach((group) => group.items.sort(compareSettings));

    return sorted;
};

const settingOrder = (setting: SystemSettingsDTO): number =>
    typeof setting.order === 'number' ? setting.order : Number.MAX_SAFE_INTEGER;

const QRPAY_SETTINGS_TOGETHER = new Set([
    'NSPK_QR_PAY_ENABLED',
    'NSPK_QR_PAY_SITES_ENABLED',
    'NSPK_QR_PAY_SITES',
]);

const settingSortWeight = (setting: SystemSettingsDTO): number => {
    if (QRPAY_SETTINGS_TOGETHER.has(getSystemSettingId(setting))) {
        return TYPE_SORT_WEIGHT[SETTING_TYPE_BOOLEAN];
    }

    return TYPE_SORT_WEIGHT[getSystemSettingType(setting)] ?? 3;
};

const compareSettings = (left: SystemSettingsDTO, right: SystemSettingsDTO): number => {
    const leftWeight = settingSortWeight(left);
    const rightWeight = settingSortWeight(right);

    if (leftWeight !== rightWeight) {
        return leftWeight - rightWeight;
    }

    return settingOrder(left) - settingOrder(right);
};

export const readStoredStringList = (key: string): string[] | null => {
    try {
        const stored = localStorage.getItem(key);
        const parsed = stored ? JSON.parse(stored) : null;
        return Array.isArray(parsed) ? parsed.filter((item) => typeof item === 'string') : null;
    } catch {
        return null;
    }
};

export const storeStringList = (key: string, list: string[]): void => {
    try {
        localStorage.setItem(key, JSON.stringify(list));
    } catch {
        return;
    }
};
